import { useNavigate } from "react-router-dom";
import type { CertificadoDoPainel } from "@/api/contrato";
import { useConferirCertificado } from "@/api/consultas";
import { useAvisos } from "@/componentes/Avisos";
import { Botao } from "@/componentes/Botao";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { Dica } from "@/componentes/Dica";
import { PainelRecolhivel } from "@/componentes/PainelRecolhivel";
import { PilulaDeExcecao } from "@/componentes/PilulaDeSituacao";
import { useModalNaUrl } from "@/componentes/useModalNaUrl";
import { vencimentoRelativo } from "@/formato";
import { useSessao } from "@/sessao/useSessao";
import { dicaDoVencimento, pilulaDoCertificado, textoDoVencimento } from "./certificado";
import { textoDaRecusa } from "./recusas";

/*
 * O certificado, no painel do convênio e na aba Dados. Um cartão, sem aba própria: separar o
 * certificado do convênio que depende dele seria mais um lugar para olhar (design D11).
 *
 * Estado normal não se anuncia: em dia, só o vencimento relativo, e a palavra `válido` fica
 * para o leitor de tela. A ação some para quem não é `admin`, e o cartão continua.
 */
export function CartaoDoCertificado({
  certificado,
  convenioID,
  travado,
  trocaEm,
}: {
  certificado: CertificadoDoPainel;
  /** O convênio do cartão. `Conferir` é por convênio: no nível cedente, a rota não existe. */
  convenioID?: string;
  /** O porquê de o envio ainda não caber: provisionar vem antes do certificado. */
  travado?: string | undefined;
  /** Onde a troca acontece, quando não é aqui: o vencido é o do cedente, e a saída é na Dados. */
  trocaEm?: string | undefined;
}) {
  const { pode } = useSessao();
  const modal = useModalNaUrl();
  const navegar = useNavigate();

  const { situacao, apelido } = certificado;
  const pilula = pilulaDoCertificado(situacao);
  const urgente = situacao === "ausente" || situacao === "vencido" || situacao === "vencendo";

  function trocar() {
    if (trocaEm === undefined) modal.abrirModal({ abrir: "certificado" });
    else void navegar(trocaEm);
  }

  return (
    <PainelRecolhivel titulo="Certificado">
      {situacao === "ausente" ? (
        <p className="text-texto-2">Sem certificado.</p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <CaixaDeCampo rotulo="Vencimento">
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="flex items-center gap-1">
                {textoDoVencimento(certificado)}
                <Dica texto={dicaDoVencimento(certificado, convenioID !== undefined)} />
              </span>
              <PilulaDeExcecao normal={pilula.normal} matiz={pilula.matiz}>
                {pilula.rotulo}
              </PilulaDeExcecao>
            </span>
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Apelido">{apelido || null}</CaixaDeCampo>
        </div>
      )}

      {pode("admin") && (
        <div className="flex items-center justify-end gap-2">
          {convenioID !== undefined && travado === undefined && (
            <BotaoConferir convenioID={convenioID} />
          )}
          {travado !== undefined && <Dica texto={travado} />}
          <Botao
            tom={urgente ? "primario" : "secundario"}
            disabled={travado !== undefined}
            onClick={trocar}
          >
            {situacao === "ausente" ? "Enviar certificado" : "Trocar certificado"}
          </Botao>
        </div>
      )}
    </PainelRecolhivel>
  );
}

/*
 * Lê a TecnoSpeed sem enviar nada: é a saída de quem renovou na GUI deles, e libera a emissão
 * na requisição seguinte. `encontrado: false` não é falha: a linha daqui fica como está.
 */
function BotaoConferir({ convenioID }: { convenioID: string }) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const conferir = useConferirCertificado(cliente, convenioID);

  function aoClicar() {
    conferir.mutate(undefined, {
      onSuccess: ({ encontrado, vencimento }) => {
        if (!encontrado) avisos.feito("Nenhum certificado na TecnoSpeed");
        else if (vencimento === null) avisos.feito("Certificado conferido");
        else avisos.feito("Certificado conferido", vencimentoRelativo(vencimento));
      },
      onError: (falha) => avisos.falhou(textoDaRecusa(falha)),
    });
  }

  return (
    <span className="mr-auto flex items-center gap-1">
      <Botao tom="secundario" carregando={conferir.isPending} onClick={aoClicar}>
        Conferir
      </Botao>
      <Dica texto="Lê o certificado na TecnoSpeed. Depois de uma troca, a sincronização lá pode levar até 1 h." />
    </span>
  );
}
